import * as String from '@wandb/weave/core/util/string';
import React, {type FC, memo, useCallback, useMemo} from 'react';
import {useLocation, useRouteMatch} from 'react-router';

import {
  useFetchJobQuery,
  useMarimoNotebookRuntimePageQuery,
  useUpsertModelMutation,
} from '../../../../generated/graphql';
import {MARIMO_NOTEBOOK_EDIT_PATH} from '../../../../routes/notebooks/notebookPaths';
import {
  ENTITY_SANDBOX_DETAIL_PATH,
  ENTITY_SANDBOXES_PATH,
  GLOBAL_SANDBOX_DETAIL_PATH,
  GLOBAL_SANDBOX_DRAWER_PATH,
} from '../../../../routes/sandboxes/sandboxesPaths';
import {getSweepDisplayName} from '../../../../util/getSweepDisplayName';
import {trackBreadcrumbClicked} from '../../../../util/navigation';
import {type AccessOption} from '../../../../util/permissions';
import {cleanTabName} from '../../../../util/string';
import * as links from '../../../../util/urls';
import {removeNullUndefinedOrFalse} from '../../../../util/utility';
import AccessSettingOptions from '../../../AccessSettingOptions';
import Breadcrumbs, {type BreadcrumbPart} from '../../../Breadcrumbs';
import {useSingleRunLink} from '../../../MultiRunWorkspace/SingleRunMode';
import {useNavContext} from '../../../NavContextProvider';
import {getRunsTableLink, type ProjObjType} from './utils';

export type NavBreadcrumbsDefaultProps = {
  entityName: string;
  reportName: string;
  reportID: string;
  projObjType: ProjObjType | undefined;
};

export function useNavBreadcrumbsDefaultParts({
  entityName,
  reportName,
  reportID,
  projObjType,
}: NavBreadcrumbsDefaultProps): BreadcrumbPart[] {
  const {
    searchData: data,
    params: {
      projectName,
      tab,
      groupName,
      jobId,
      jobTab,
      queueId,
      sandboxId,
      reportNameAndID,
      runName,
    },
  } = useNavContext();
  const {pathname} = useLocation();
  const notebookEditMatch = useRouteMatch<{notebookId: string}>(
    MARIMO_NOTEBOOK_EDIT_PATH
  );
  const notebookId = notebookEditMatch?.params.notebookId;
  const isEntitySandboxesPage =
    useRouteMatch([ENTITY_SANDBOXES_PATH, ENTITY_SANDBOX_DETAIL_PATH]) != null;
  const globalSandboxDetailMatch = useRouteMatch([
    GLOBAL_SANDBOX_DRAWER_PATH,
    GLOBAL_SANDBOX_DETAIL_PATH,
  ]);
  const isGlobalSandboxesPage =
    !isEntitySandboxesPage && globalSandboxDetailMatch != null;
  const isSandboxesPage = isEntitySandboxesPage || isGlobalSandboxesPage;
  const isEditingReport =
    pathname.includes('/reports/') && pathname.endsWith('/edit');

  const draftId = new URLSearchParams(window.location.search).get('draftId');
  const queryJob = useFetchJobQuery({
    variables: {
      jobId: jobId ?? '',
    },
    skip: jobId == null,
  });
  const decodedNotebookId =
    notebookId == null ? '' : links.decodeURIComponentSafe(notebookId);
  const queryNotebook = useMarimoNotebookRuntimePageQuery({
    variables: {id: decodedNotebookId},
    skip: notebookId == null,
  });

  const project = data?.project;
  const sweepName = project?.sweep?.name;
  const sweepDisplayName = getSweepDisplayName(project?.sweep);
  const jobDisplayName = queryJob?.data?.artifactCollection?.name;
  const queriedNotebook = queryNotebook.data?.notebook;
  const isNotebookInProject =
    queriedNotebook != null &&
    queriedNotebook.project.entityName === entityName &&
    queriedNotebook.project.name === projectName;
  const notebookDisplayName = isNotebookInProject
    ? queriedNotebook.name
    : undefined;
  const runDisplayName = project?.run?.displayName;

  const [upsertModelMutation] = useUpsertModelMutation();
  const updateProjectAccess = useCallback(
    async (access: AccessOption) => {
      if (projectName != null && project != null) {
        await upsertModelMutation({
          variables: {
            entityName,
            name: projectName,
            access,
          },
        });
      }
    },
    [upsertModelMutation, entityName, projectName, project]
  );

  const projObjName =
    groupName || sweepName || jobId || reportNameAndID || runName;

  const tabName = tab === 'reportlist' ? 'reports' : tab;
  const tabDisplayName =
    tabName === 'notebooks' ? 'Marimo notebooks' : cleanTabName(tabName ?? '');

  const runLinkArgs =
    projectName != null && runName != null
      ? {
          entityName,
          projectName,
          runName,
        }
      : undefined;
  const runLink = useSingleRunLink(runLinkArgs);

  const parts: BreadcrumbPart[] = useMemo(
    () =>
      removeNullUndefinedOrFalse([
        {
          label: entityName,
          to: `/${entityName}`,
          onClick: () => trackBreadcrumbClicked('Entity'),
        },

        isSandboxesPage && {
          label: 'Sandboxes',
          to: isGlobalSandboxesPage
            ? links.globalSandboxesList()
            : links.entitySandboxes(entityName),
          onClick: () => trackBreadcrumbClicked('Sandboxes'),
        },

        isSandboxesPage &&
          sandboxId != null && {
            label: sandboxId,
          },

        queueId != null && {
          label: `Launch`,
          to: links.launchOverview(),
          onClick: () => trackBreadcrumbClicked('Launch'),
        },

        projectName != null && {
          label: `Projects`,
          to: `/${entityName}/projects`,
          onClick: () => trackBreadcrumbClicked('Projects'),
        },

        projectName != null && {
          beforeLabel: project != null && updateProjectAccess != null && (
            <AccessSettingOptions
              accessSettings={{
                project: {
                  id: project.id,
                  name: project.name,
                  access: project.access,
                  user: project.user,
                },
                entity: project.entity,
              }}
              compact
              updateProjectAccess={updateProjectAccess}
            />
          ),
          label: projectName,
          to: links.project({entityName, name: projectName}),
          onClick: () => trackBreadcrumbClicked('Project'),
        },

        ...getLaunchBreadcrumbParts({
          entityName,
          projectName,
          sweepName,
          sweepDisplayName,
          jobId,
          jobDisplayName,
          jobTab,
        }),

        projectName != null &&
          groupName != null && {
            label: `Run groups`,
          },
        projectName != null &&
          groupName != null && {
            label: groupName,
            to: links.runGroup({entityName, projectName, name: groupName}),
            onClick: () => trackBreadcrumbClicked('Group'),
          },

        projectName != null &&
          runName != null && {
            label: `Runs`,
            to: getRunsTableLink({
              entityName,
              projectName,
              groupName,
              sweepName,
            }),
            onClick: () => trackBreadcrumbClicked('Runs'),
          },
        projectName != null &&
          runName != null && {
            label: runDisplayName || runName,
            to: runLink,
            onClick: () => {
              let itemClicked = 'Run';
              if (groupName != null) {
                itemClicked = 'Group run';
              } else if (sweepName != null) {
                itemClicked = 'Sweep run';
              }
              trackBreadcrumbClicked(itemClicked);
            },
          },

        projectName != null &&
          reportNameAndID != null && {
            label: `Reports`,
            to: links.reportList({entityName, projectName}),
            onClick: () => trackBreadcrumbClicked('Reports'),
          },
        projectName != null &&
          reportNameAndID != null && {
            label: reportName,
            to:
              isEditingReport && draftId != null
                ? links.reportEdit(
                    {
                      entityName,
                      projectName,
                      reportID,
                      reportName,
                    },
                    `?draftId=${draftId}`
                  )
                : links.reportView({
                    entityName,
                    projectName,
                    reportID,
                    reportName,
                  }),
            onClick: () => trackBreadcrumbClicked('Report'),
          },

        projectName != null &&
          tabName != null &&
          tab != null &&
          tab !== 'runs' && // "Runs" breadcrumb is already handled above
          jobTab == null && {
            label: tabDisplayName,
            to: links.projectObjTab(
              entityName,
              projectName,
              projObjType,
              projObjName,
              tab
            ),
            onClick: () => {
              if (tabName === 'artifacts') {
                trackBreadcrumbClicked(String.capitalizeFirst(tabName));
              }
            },
          },

        notebookId != null &&
          notebookDisplayName != null && {
            label: notebookDisplayName,
          },
      ]),
    [
      entityName,
      isSandboxesPage,
      isGlobalSandboxesPage,
      sandboxId,
      queueId,
      projectName,
      project,
      updateProjectAccess,
      sweepName,
      sweepDisplayName,
      jobId,
      jobDisplayName,
      jobTab,
      notebookId,
      notebookDisplayName,
      runName,
      runDisplayName,
      runLink,
      groupName,
      reportNameAndID,
      reportName,
      isEditingReport,
      reportID,
      tabName,
      tabDisplayName,
      tab,
      projObjType,
      projObjName,
      draftId,
    ]
  );

  return parts;
}

const NavBreadcrumbsDefaultComp: FC<NavBreadcrumbsDefaultProps> = params => {
  const parts = useNavBreadcrumbsDefaultParts(params);
  return <Breadcrumbs parts={parts} />;
};

const NavBreadcrumbsDefault = memo(NavBreadcrumbsDefaultComp);

export default NavBreadcrumbsDefault;

type GetLaunchBreadcrumbPartsParams = {
  entityName: string;
  projectName: string | undefined;
  sweepName: string | undefined;
  sweepDisplayName: string;
  jobId: string | undefined;
  jobDisplayName: string | undefined;
  jobTab: string | undefined;
};

function getLaunchBreadcrumbParts({
  entityName,
  projectName,
  sweepName,
  sweepDisplayName,
  jobId,
  jobDisplayName,
  jobTab,
}: GetLaunchBreadcrumbPartsParams): BreadcrumbPart[] {
  if (projectName == null) {
    return [];
  }

  return removeNullUndefinedOrFalse([
    sweepName != null && {
      label: `Sweeps`,
      to: links.projectSweeps({entityName, name: projectName}),
      onClick: () => trackBreadcrumbClicked('Sweeps'),
    },
    sweepName != null &&
      sweepDisplayName != null && {
        label: sweepDisplayName,
        to: links.sweep({entityName, projectName, sweepName}),
        onClick: () => trackBreadcrumbClicked('Sweep'),
      },

    jobId != null && {
      label: `Jobs`,
      to: links.jobs({entityName, projectName}),
      onClick: () => trackBreadcrumbClicked('Jobs'),
    },
    jobId != null &&
      jobDisplayName != null && {
        label: jobDisplayName,
        to: links.job({entityName, projectName, jobId}),
        onClick: () => trackBreadcrumbClicked('Job'),
      },
    jobId != null &&
      jobDisplayName != null &&
      jobTab != null && {
        label: cleanTabName(jobTab),
        to: links.job({entityName, projectName, jobId, jobTab}),
        onClick: () => trackBreadcrumbClicked('JobTab'),
      },
  ]);
}
